import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";
import { useBlogPost } from "@/hooks/useBlogPosts";
import Footer from "@/components/Footer";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { extractHeadings } from "@/lib/tocParser";
import { TableOfContents } from "@/components/blog/TableOfContents";
import { SchemaMarkup } from "@/components/blog/SchemaMarkup";
import { generateBlogPostSchema } from "@/lib/schemaGenerator";
import { AuthorBio } from "@/components/blog/AuthorBio";
import { calculateReadTime, formatReadTime } from "@/lib/readTimeCalculator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMemo } from "react";
import { generateCanonicalUrl } from "@/lib/seoConfig";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const BlogPost = () => {
  const { slug } = useParams();
  const { data: post, isLoading } = useBlogPost(slug || "");

  const headings = useMemo(() => {
    if (!post?.content) return [];
    return extractHeadings(post.content);
  }, [post?.content]);

  const readTime = useMemo(() => {
    if (!post?.content) return 0;
    return calculateReadTime(post.content);
  }, [post?.content]);

  const schema = useMemo(() => {
    if (!post) return null;
    return generateBlogPostSchema({
      title: post.title,
      excerpt: post.excerpt || undefined,
      content: post.content,
      slug: post.slug,
      publishedAt: post.published_at || post.created_at,
      updatedAt: post.updated_at,
      author: post.profiles || undefined,
      imageUrl: post.featured_image_url || undefined,
    });
  }, [post]);

  const author = post?.profiles;
  const authorName = author?.full_name || "iQ Team";
  const authorInitials = authorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const isUpdated = post && post.updated_at && post.published_at && 
    new Date(post.updated_at).getTime() > new Date(post.published_at).getTime() + 60000;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | iQ Texting Blog</title>
        <meta name="description" content={post.excerpt || post.content.substring(0, 160)} />
        <link rel="canonical" href={generateCanonicalUrl(`/blog/${slug}`)} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.content.substring(0, 160)} />
        <meta property="og:url" content={generateCanonicalUrl(`/blog/${slug}`)} />
        <meta property="og:type" content="article" />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.content.substring(0, 160)} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {schema && <SchemaMarkup schema={schema} />}
      
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" asChild>
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs 
            items={[
              { name: "Blog", url: "/blog" },
              { name: post.title, url: `/blog/${slug}` }
            ]} 
          />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
            {/* Main Content */}
            <article className="min-w-0">
              <header className="mb-8">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{post.title}</h1>
                {post.excerpt && (
                  <p className="text-xl text-muted-foreground mb-6">{post.excerpt}</p>
                )}
                
                {/* Author and Metadata */}
                <div className="flex items-center gap-6 text-sm mb-4">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={author?.avatar_url || ""} alt={authorName} />
                      <AvatarFallback>{authorInitials}</AvatarFallback>
                    </Avatar>
                    <div>
                      {author?.author_slug ? (
                        <Link 
                          to={`/blog/author/${author.author_slug}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {authorName}
                        </Link>
                      ) : (
                        <p className="font-medium">{authorName}</p>
                      )}
                      {author?.job_title && (
                        <p className="text-xs text-muted-foreground">{author.job_title}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(post.published_at || post.created_at).toLocaleDateString()}
                  </div>
                  {isUpdated && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Updated {new Date(post.updated_at).toLocaleDateString()}
                    </div>
                  )}
                  {readTime > 0 && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatReadTime(readTime)}
                    </div>
                  )}
                </div>
              </header>

              <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({ children, ...props }) => {
                      const text = String(children);
                      const id = text
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      return <h2 id={id} {...props}>{children}</h2>;
                    },
                    h3: ({ children, ...props }) => {
                      const text = String(children);
                      const id = text
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      return <h3 id={id} {...props}>{children}</h3>;
                    },
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Author Bio Box */}
              {author && <AuthorBio author={author} />}
            </article>

            {/* Table of Contents - Desktop */}
            {headings.length > 0 && (
              <aside className="hidden lg:block">
                <TableOfContents headings={headings} />
              </aside>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default BlogPost;
